import graphql from '@/mixins/graphql';
import { gql } from 'graphql-request';
import lodash from 'lodash';

export default {
  mixins: [graphql],
  beforeCreate() {
    this.$graphql.files.requestConfig.requestMiddleware = request =>
      this.graphqlRequestMiddleware(request);
    this.$graphql.files.requestConfig.responseMiddleware = response =>
      this.graphqlResponseMiddleware(response);
  },
  methods: {
    async getPreSignedLinks(files, handleUploadError) {
      const preSignedLinkQueries = files
        .map((file, index) => {
          return gql`
          file${index}: getPreSignedLink(type: "${file.mimeType}", size: ${file.size}) {
            id
            preSignedUrl
          }`;
        })
        .join('');
      const preSignedLinksQuery = gql`query {${preSignedLinkQueries}}`;
      const clonedFiles = lodash.cloneDeep(files);

      try {
        return await this.$graphql.files.request(preSignedLinksQuery);
      } catch (err) {
        const errors = err.response.errors;
        if (errors?.length) {
          for (const error of errors) {
            const index = parseInt(lodash.nth(error.path, 0)?.slice(4) || '0', 10);
            switch (error.message) {
              case 'unsupported-extension':
              case 'file-size-limit':
              case 'request-size-limit':
                handleUploadError({
                  message: error.message,
                  fileName: error.path?.length ? clonedFiles[index].name : undefined
                });
                break;
              default:
                this.handleError({ error, code: '0x00000036' });
                break;
            }
          }
          return;
        }

        this.handleError({ error: e, code: '0x00000037' });
      }
    },
    async uploadFile(url, buffer, type) {
      try {
        await this.$http.put(url, buffer, {
          raw: true,
          headers: {
            'Content-Type': type
          }
        });
      } catch (err) {
        this.handleError({ error: err, code: '0x00000038' });
      }
    },
    async getFileObjectMappings(filesWithPreSignedData, handleUploadError) {
      const createFileMutations = filesWithPreSignedData
        .map(file => {
          return gql`
          ${file.key}: createFile(id: "${file.awsId}", name: "${file.name}", type: "${file.mimeType}") {
            id
            internalId
          }`;
        })
        .join('');
      const createFilesMutation = gql`mutation {${createFileMutations}}`;

      try {
        return await this.$graphql.files.request(createFilesMutation);
      } catch (err) {
        const errors = err.response.errors;
        if (errors?.length) {
          for (const error of errors) {
            const index = parseInt(lodash.nth(error.path, 0)?.slice(4) || '0', 10);
            switch (error.message) {
              case 'unsupported-extension':
              case 'file-size-limit':
              case 'request-size-limit':
              case 'internal-server-error':
                handleUploadError({
                  message: error.message,
                  fileName: error.path?.length ? filesWithPreSignedData[index].name : undefined
                });
                break;
              default:
                this.logToFlutter({
                  level: 'E',
                  message: 'SPA HANDLE ERROR 57'
                });
                this.handleError({ error, code: '0x00000039' });
                break;
            }
          }
          return;
        }

        this.handleError({ error: e, code: '0x00000079' });
      }
    },
    async processFiles(files, handleUploadError) {
      const preSignedData = await this.getPreSignedLinks(files, handleUploadError);
      if (!preSignedData) {
        return;
      }

      const extendedFiles = files.map((file, index) => {
        const key = `file${index}`;
        return {
          ...file,
          key,
          awsId: preSignedData[key].id,
          preSignedUrl: preSignedData[key].preSignedUrl
        };
      });

      await Promise.all(
        extendedFiles.map(
          async att => await this.uploadFile(att.preSignedUrl, att.fileBuffer, att.mimeType)
        )
      );

      const fileObjectMappings = await this.getFileObjectMappings(extendedFiles, handleUploadError);
      if (!fileObjectMappings) {
        return;
      }

      return extendedFiles.map(file => {
        return {
          id: fileObjectMappings[file.key].internalId,
          awsId: file.awsId,
          name: file.name,
          size: file.size,
          preSignedUrl: file.preSignedUrl
        };
      });
    },
    handlePreSignedError(error, identifier, afterEmitCallback = () => {}) {
      if (error.message === 'internal-server-error') {
        this.handleError({ error, code: '0x0000003B' });
        return;
      }

      let errorForFileInput = {};
      switch (error.message) {
        case 'unsupported-extension':
          errorForFileInput = { errorType: 'type', fileName: error.fileName };
          break;
        case 'file-size-limit':
          errorForFileInput = { errorType: 'size', fileName: error.fileName };
          break;
        case 'request-size-limit':
          errorForFileInput = { errorType: 'totalSize' };
          break;
      }

      this.$eventHub.$emit(`fileInput_${identifier}_handleOutsideError`, errorForFileInput);
      afterEmitCallback();
    }
  }
};
